import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import Logo from './Logo';
import { NAV_LINKS, RETAIL_URL } from '../content/site';

export default function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const onHomeClick = (e) => {
    if (location.pathname === '/commercial') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-line bg-base/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-shell items-center justify-between px-5 sm:px-8">
        <Link to="/commercial" onClick={onHomeClick} className="shrink-0" data-testid="nav-home-link" aria-label="RangeSafe Commercial Solutions home">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.href}
              to={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${
                  isActive ? 'text-white' : 'text-dim hover:text-white'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-5 lg:flex">
          <a
            href={RETAIL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-retail-link"
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mute transition-colors hover:text-dim"
          >
            Retail Purchases
            <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
          </a>
          <Link
            to="/quote"
            data-testid="nav-quote-cta"
            className="inline-flex h-10 items-center border border-accent bg-accent px-5 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-white transition-colors hover:bg-accent2 hover:border-accent2"
          >
            Request a Consultation
          </Link>
        </div>

        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center border border-line text-white lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
        </button>
        </div>
      </header>

      {open && (
        <div
          id="mobile-menu"
          data-testid="mobile-menu"
          className="fixed inset-x-0 bottom-0 top-16 z-40 overflow-y-auto border-t border-line bg-base lg:hidden"
        >
          <nav className="flex flex-col px-5 py-8 sm:px-8" aria-label="Mobile">
            {NAV_LINKS.map((l, i) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex items-baseline justify-between border-b border-line2 py-5"
              >
                <span className="font-display text-4xl font-bold uppercase tracking-tight text-white">
                  {l.label}
                </span>
                <span className="font-mono text-[10px] text-mute">0{i + 1}</span>
              </Link>
            ))}
            <Link
              to="/quote"
              data-testid="mobile-quote-cta"
              className="mt-8 inline-flex h-14 w-full items-center justify-center border border-accent bg-accent font-mono text-xs font-medium uppercase tracking-[0.22em] text-white"
            >
              Request a Consultation
            </Link>
            <a
              href={RETAIL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-retail-link"
              className="mt-6 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mute"
            >
              Retail Purchases
              <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
            </a>
          </nav>
        </div>
      )}
    </>
  );
}
